import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import DropdownList from './DropdownList';

/**
 * Fixed-option select with the editor's unified chrome (owner, 30.08): a
 * `modal-input`-framed button trigger + the shared DropdownList — replaces
 * every native <select> in the event editor (reminders, calendar picker) so
 * no browser-default dropdown remains. No free typing here: the option list
 * IS the domain (that's what separates it from TimeCombobox).
 *
 * options: [{ value, label }]
 */
export default function EditorSelect({ value, options, onChange, ariaLabel, width = 170 }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value) || null;

  const toggle = () => {
    if (!open) {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect(r);
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // The modal-input frame — one border language across title input,
        // date triggers, time comboboxes and these selects, both themes.
        className="modal-input"
        role="combobox"
        aria-expanded={open}
        // Only while the list exists: it is rendered on open, and pointing at an
        // id that is not in the document says nothing to a screen reader.
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        style={{
          width,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          textAlign: 'left',
        }}
        data-editor-dropdown="true"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.label : ''}
        </span>
        <ChevronDown size={13} style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>
      {open && (
        <DropdownList
          id={listId}
          anchorRect={rect}
          ariaLabel={t('calendar.editor.optionsFor', { label: ariaLabel })}
          onRequestClose={() => setOpen(false)}
          items={options.map((o) => ({
            key: String(o.value),
            label: o.label,
            selected: o.value === value,
            onPick: () => {
              onChange?.(o.value);
              setOpen(false);
            },
          }))}
        />
      )}
    </>
  );
}
